import { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../context/AuthContext';
import { Navigate } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Textarea } from '../components/ui/textarea';
import { toast } from 'sonner';
import {
  ShieldCheck, Users, FileText, Award, Clock, CheckCircle, XCircle, Eye,
  BarChart3, AlertCircle, Download
} from 'lucide-react';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

function getRoleBadgeClass(role) {
  if (role === 'admin') return 'bg-cabernet/10 text-cabernet border-cabernet/30';
  if (role === 'donor') return 'bg-brass/20 text-ink border-brass/30';
  return 'bg-blue-50 text-blue-700 border-blue-200';
}

export default function AdminPanel() {
  const { user } = useAuth();
  const [applications, setApplications] = useState([]);
  const [stats, setStats] = useState(null);
  const [allUsers, setAllUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedApp, setSelectedApp] = useState(null);
  const [newStatus, setNewStatus] = useState('');
  const [adminNotes, setAdminNotes] = useState('');
  const [updating, setUpdating] = useState(false);

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const [appsRes, statsRes, usersRes] = await Promise.all([
        axios.get(`${API}/admin/applications`, { withCredentials: true }),
        axios.get(`${API}/admin/stats`, { withCredentials: true }),
        axios.get(`${API}/admin/users`, { withCredentials: true }),
      ]);
      setApplications(appsRes.data);
      setStats(statsRes.data);
      setAllUsers(usersRes.data);
    } catch (err) {
      console.error('Failed to load admin data:', err.message);
      toast.error('Failed to load admin data');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (user?.role === 'admin') {
      fetchData();
    }
  }, [user, fetchData]);

  const handleStatusUpdate = async () => {
    if (!selectedApp || !newStatus) return;
    setUpdating(true);
    try {
      await axios.put(
        `${API}/admin/applications/${selectedApp.application_id}/status`,
        { status: newStatus, admin_notes: adminNotes || null },
        { withCredentials: true }
      );
      toast.success(`Application status updated to "${newStatus}"`);
      setSelectedApp(null);
      setNewStatus('');
      setAdminNotes('');
      fetchData();
    } catch {
      toast.error('Failed to update status');
    } finally {
      setUpdating(false);
    }
  };

  const handleDownloadFile = async (fileId, filename) => {
    try {
      const res = await axios.get(`${API}/files/${fileId}/download`, {
        withCredentials: true,
        responseType: 'blob',
      });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement('a');
      a.href = url;
      a.download = filename || 'essay';
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
    } catch {
      toast.error('Failed to download file');
    }
  };

  if (user?.role !== 'admin') {
    return <Navigate to="/dashboard" replace />;
  }

  if (loading) {
    return (
      <div className="min-h-screen bg-cream flex items-center justify-center">
        <div className="w-8 h-8 border-4 border-cabernet border-t-transparent rounded-full animate-spin"></div>
      </div>
    );
  }

  const statusConfig = {
    submitted: { icon: Clock, label: 'Submitted', color: 'bg-amber-100 text-amber-800 border-amber-200' },
    reviewed: { icon: Eye, label: 'Reviewed', color: 'bg-blue-100 text-blue-800 border-blue-200' },
    awarded: { icon: CheckCircle, label: 'Awarded', color: 'bg-brass/30 text-ink border-brass' },
    rejected: { icon: XCircle, label: 'Rejected', color: 'bg-red-100 text-red-800 border-red-200' },
  };

  return (
    <div className="min-h-screen bg-cream" data-testid="admin-panel">
      <div className="max-w-7xl mx-auto px-4 sm:px-8 py-8 sm:py-12">
        {/* Header */}
        <div className="flex items-center gap-3 mb-8">
          <ShieldCheck className="w-8 h-8 text-cabernet" />
          <div>
            <h1 className="text-3xl sm:text-4xl font-heading font-medium text-cabernet tracking-tight">
              Admin Panel
            </h1>
            <p className="font-body text-sm text-ink/60">
              Manage scholarship applications and review submissions
            </p>
          </div>
        </div>

        {/* Stats Overview */}
        {stats && (
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3 mb-8" data-testid="admin-stats">
            {[
              { label: 'Total Apps', value: stats.total_applications, icon: FileText },
              { label: 'Submitted', value: stats.submitted, icon: Clock },
              { label: 'Reviewed', value: stats.reviewed, icon: Eye },
              { label: 'Awarded', value: stats.awarded, icon: Award },
              { label: 'Rejected', value: stats.rejected, icon: XCircle },
              { label: 'Users', value: stats.total_users, icon: Users },
            ].map((stat) => (
              <div key={stat.label} className="bg-card-surface rounded-lg border border-brass/20 shadow-sm p-4 text-center">
                <stat.icon className="w-5 h-5 text-brass mx-auto mb-2" />
                <p className="text-2xl font-heading font-semibold text-cabernet">{stat.value}</p>
                <p className="font-accent text-xs text-ink/60">{stat.label}</p>
              </div>
            ))}
          </div>
        )}

        <Tabs defaultValue="applications" className="space-y-6">
          <TabsList className="bg-card-surface border border-brass/20">
            <TabsTrigger value="applications" className="font-accent text-sm data-[state=active]:bg-cabernet data-[state=active]:text-cream" data-testid="admin-tab-applications">
              <FileText className="w-4 h-4 mr-1" />
              Applications ({applications.length})
            </TabsTrigger>
            <TabsTrigger value="users" className="font-accent text-sm data-[state=active]:bg-cabernet data-[state=active]:text-cream" data-testid="admin-tab-users">
              <Users className="w-4 h-4 mr-1" />
              Users ({allUsers.length})
            </TabsTrigger>
          </TabsList>

          {/* Applications Tab */}
          <TabsContent value="applications">
            {/* Review Modal */}
            {selectedApp && (
              <div className="bg-card-surface rounded-lg border-2 border-brass shadow-md p-6 mb-6" data-testid="review-modal">
                <div className="flex items-start justify-between mb-4">
                  <h3 className="text-xl font-heading font-medium text-cabernet">
                    Review Application
                  </h3>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => { setSelectedApp(null); setNewStatus(''); setAdminNotes(''); }}
                    className="text-ink/50 hover:text-ink"
                    data-testid="close-review-btn"
                  >
                    <XCircle className="w-5 h-5" />
                  </Button>
                </div>

                <div className="grid sm:grid-cols-2 gap-4 mb-4">
                  <div>
                    <p className="font-accent text-xs text-ink/60 uppercase tracking-wider">Applicant</p>
                    <p className="font-body text-sm text-ink font-medium">{selectedApp.applicant_name}</p>
                    <p className="font-body text-xs text-ink/50">{selectedApp.applicant_email}</p>
                  </div>
                  <div>
                    <p className="font-accent text-xs text-ink/60 uppercase tracking-wider">Essay Title</p>
                    <p className="font-body text-sm text-ink">{selectedApp.essay_title}</p>
                  </div>
                  <div>
                    <p className="font-accent text-xs text-ink/60 uppercase tracking-wider">Submitted</p>
                    <p className="font-body text-sm text-ink">{new Date(selectedApp.submitted_at).toLocaleDateString()}</p>
                  </div>
                  <div>
                    <p className="font-accent text-xs text-ink/60 uppercase tracking-wider">Current Status</p>
                    <Badge className={`${statusConfig[selectedApp.status]?.color || ''} font-accent text-xs`}>
                      {statusConfig[selectedApp.status]?.label || selectedApp.status}
                    </Badge>
                  </div>
                </div>

                {selectedApp.admin_notes && (
                  <div className="mb-4 p-3 bg-cream rounded-md border border-brass/20">
                    <p className="font-accent text-xs text-ink/60 uppercase tracking-wider mb-1">Previous Notes</p>
                    <p className="font-body text-sm text-ink/70">{selectedApp.admin_notes}</p>
                  </div>
                )}

                {selectedApp.file_id && (
                  <div className="mb-4">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => handleDownloadFile(selectedApp.file_id, selectedApp.essay_title)}
                      className="border-brass text-cabernet hover:bg-brass/10 font-accent text-xs"
                      data-testid="admin-download-essay-btn"
                    >
                      <Download className="w-3.5 h-3.5 mr-1" />
                      Download Essay File
                    </Button>
                  </div>
                )}

                <div className="space-y-3">
                  <div>
                    <label className="font-accent text-sm font-medium text-ink block mb-1">Update Status</label>
                    <Select value={newStatus} onValueChange={setNewStatus}>
                      <SelectTrigger className="bg-cream border-cabernet/20" data-testid="status-select-trigger">
                        <SelectValue placeholder="Select new status..." />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="reviewed">Reviewed</SelectItem>
                        <SelectItem value="awarded">Awarded</SelectItem>
                        <SelectItem value="rejected">Rejected</SelectItem>
                        <SelectItem value="submitted">Reset to Submitted</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <label className="font-accent text-sm font-medium text-ink block mb-1">Admin Notes (optional)</label>
                    <Textarea
                      value={adminNotes}
                      onChange={(e) => setAdminNotes(e.target.value)}
                      placeholder="Add notes about this decision..."
                      className="bg-cream border-cabernet/20 font-body text-sm"
                      data-testid="admin-notes-input"
                    />
                  </div>
                  <Button
                    onClick={handleStatusUpdate}
                    disabled={!newStatus || updating}
                    className="bg-cabernet text-cream hover:bg-cabernet-hover font-accent text-sm"
                    data-testid="update-status-btn"
                  >
                    {updating ? 'Updating...' : 'Update Status'}
                  </Button>
                </div>
              </div>
            )}

            {/* Applications List */}
            <div className="space-y-3" data-testid="admin-applications-list">
              {applications.length === 0 ? (
                <div className="bg-card-surface rounded-lg border border-brass/20 p-12 text-center">
                  <AlertCircle className="w-10 h-10 text-brass/50 mx-auto mb-3" />
                  <p className="font-body text-ink/60">No applications submitted yet.</p>
                </div>
              ) : (
                applications.map((app) => {
                  const config = statusConfig[app.status] || statusConfig.submitted;
                  const StatusIcon = config.icon;
                  return (
                    <div
                      key={app.application_id}
                      className="bg-card-surface rounded-lg border border-brass/20 shadow-sm p-5 flex flex-col sm:flex-row sm:items-center gap-4"
                      data-testid={`admin-app-card-${app.application_id}`}
                    >
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 mb-1">
                          <p className="font-heading text-base font-medium text-cabernet truncate">
                            {app.applicant_name}
                          </p>
                          <Badge className={`${config.color} font-accent text-xs flex items-center gap-1 shrink-0 border`}>
                            <StatusIcon className="w-3 h-3" />
                            {config.label}
                          </Badge>
                        </div>
                        <p className="font-body text-sm text-ink/70 truncate">
                          Essay: "{app.essay_title}"
                        </p>
                        <p className="font-accent text-xs text-ink/50 mt-1">
                          {app.applicant_email} &middot; {new Date(app.submitted_at).toLocaleDateString()}
                          {app.file_id && <span className="ml-2 text-brass"> &middot; Essay attached</span>}
                        </p>
                      </div>
                      <Button
                        onClick={() => { setSelectedApp(app); setNewStatus(''); setAdminNotes(''); }}
                        variant="outline"
                        size="sm"
                        className="border-cabernet text-cabernet hover:bg-cabernet/5 font-accent text-xs shrink-0"
                        data-testid={`review-btn-${app.application_id}`}
                      >
                        <Eye className="w-3.5 h-3.5 mr-1" />
                        Review
                      </Button>
                    </div>
                  );
                })
              )}
            </div>
          </TabsContent>

          {/* Users Tab */}
          <TabsContent value="users">
            <div className="bg-card-surface rounded-lg border border-brass/20 shadow-sm overflow-hidden" data-testid="admin-users-table">
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead>
                    <tr className="border-b border-brass/20 bg-cream">
                      <th className="text-left px-4 py-3 font-accent text-xs text-ink/60 uppercase tracking-wider">Name</th>
                      <th className="text-left px-4 py-3 font-accent text-xs text-ink/60 uppercase tracking-wider">Email</th>
                      <th className="text-left px-4 py-3 font-accent text-xs text-ink/60 uppercase tracking-wider">Role</th>
                      <th className="text-left px-4 py-3 font-accent text-xs text-ink/60 uppercase tracking-wider">Joined</th>
                    </tr>
                  </thead>
                  <tbody>
                    {allUsers.map((u) => (
                      <tr key={u.user_id} className="border-b border-brass/10 last:border-0 hover:bg-cream/50">
                        <td className="px-4 py-3">
                          <div className="flex items-center gap-2">
                            {u.picture ? (
                              <img src={u.picture} alt="" className="w-6 h-6 rounded-full" />
                            ) : (
                              <div className="w-6 h-6 rounded-full bg-cabernet/10 flex items-center justify-center">
                                <Users className="w-3 h-3 text-cabernet" />
                              </div>
                            )}
                            <span className="font-body text-sm text-ink">{u.name}</span>
                          </div>
                        </td>
                        <td className="px-4 py-3 font-accent text-xs text-ink/70">{u.email}</td>
                        <td className="px-4 py-3">
                          <Badge className={`font-accent text-xs ${
                            getRoleBadgeClass(u.role)
                          } border`}>
                            {u.role}
                          </Badge>
                        </td>
                        <td className="px-4 py-3 font-accent text-xs text-ink/50">
                          {u.created_at ? new Date(u.created_at).toLocaleDateString() : '-'}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          </TabsContent>
        </Tabs>
      </div>
    </div>
  );
}
